import { ChevronDown } from 'lucide-react'
// src/pages/Settings.tsx — Platform Settings & Access Control
// Tier A only (full access) + Tier B PM (job assignment panel only)
// Implements the four-tier access model per spec May 2026

import { useState } from 'react'
import { setNightShiftLoading, getPayPeriod, setPayPeriod,
  setWeeklyHoursCap, getWeeklyHoursCap, setClassificationOTThresholds,
  getClassificationOTThresholds } from '../utils/mockDataExtended'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_USERS_REGISTRY, MOCK_ACTIVE_SESSIONS, MOCK_AUDIT_LOG,
  FEATURE_ACCESS_CONFIG, TIER_META, PO_THRESHOLDS,
  PlatformUser,
  AuditEvent, AUDIT_EVENT_META, sessionsPerUser,
} from '../utils/mockDataUsers'
import { MOCK_JOBS, PROJECT_TYPE_META } from '../utils/mockData'
import { initialsFrom } from '../stores/auth.store'
import { formatDate } from '../utils/formatters'
import { enableTestMode, disableTestMode, isTestMode } from '../utils/platformMode'
import {
  Calculator, Users, Shield, Settings, Lock, FileText, AlertTriangle,
  CheckCircle, X, Plus, Briefcase,
  Monitor, LogOut, Edit, UserCheck, UserX, Key, Globe, Activity
} from 'lucide-react'

// ── Tier badge ─────────────────────────────────────────────────────
function TierBadge({ tier }: { tier: string }) {
  const m = TIER_META[tier] ?? TIER_META.D_FIELD
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${m.bg} ${m.text}`}>
      {m.short}
    </span>
  )
}

// ── Status badge ───────────────────────────────────────────────────
function StatusBadge({ active, lockedUntil }: { active: boolean; lockedUntil: string | null }) {
  if (lockedUntil) return <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">Locked</span>
  if (!active)     return <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">Inactive</span>
  return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Active</span>
}

// ── Create/Edit User Modal ─────────────────────────────────────────
function CreateUserModal({ onClose, onSave }: { onClose: () => void; onSave: (u: Partial<PlatformUser>) => void }) {
  const [form, setForm] = useState({
    email: '', fullName: '', jobTitle: '', tier: 'B_PC' as PlatformUser['tier'],
    mfaEnabled: true, notes: '',
  })
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b bg-blue-50 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Create user account</h2>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Full name <span className="text-red-500">*</span></label>
            <input className="input" value={form.fullName} onChange={e => set('fullName', e.target.value)} required placeholder="e.g. Sarah Lee" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
            <input type="email" className="input" value={form.email} onChange={e => set('email', e.target.value)} required placeholder="email@trafficprocure.local" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Job title</label>
            <input className="input" value={form.jobTitle} onChange={e => set('jobTitle', e.target.value)} placeholder="e.g. Project Coordinator" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Access tier <span className="text-red-500">*</span></label>
            <select className="input" value={form.tier} onChange={e => set('tier', e.target.value)}>
              {Object.entries(TIER_META).map(([t, m]) => (
                <option key={t} value={t}>{m.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">{TIER_META[form.tier]?.description}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => set('mfaEnabled', !form.mfaEnabled)}
              className={`w-10 h-6 rounded-full transition-colors ${form.mfaEnabled ? 'bg-blue-600' : 'bg-gray-300'}`}>
              <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform mx-1 ${form.mfaEnabled ? 'translate-x-4' : ''}`} />
            </button>
            <label className="text-sm text-gray-700">Require MFA on first login</label>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
            <textarea className="input h-16 resize-none" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional notes…" />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            A temporary password will be sent to the employee's registered email or mobile.
            They must change it on first login.
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={() => { onSave(form); onClose() }} disabled={!form.email || !form.fullName}
            className="btn-primary disabled:opacity-50">Create account</button>
        </div>
      </div>
    </div>
  )
}

// ── USER ACCOUNTS TAB ──────────────────────────────────────────────
function UserAccountsTab({ isAdmin }: { isAdmin: boolean }) {
  const [users, setUsers]       = useState(MOCK_USERS_REGISTRY)
  const [showCreate, setShowCreate] = useState(false)
  const [filterTier, setFilterTier] = useState('ALL')
  const [filterStatus, setFilterStatus] = useState('ALL')
  const sessionCounts = sessionsPerUser()

  const filtered = users.filter(u => {
    if (filterTier !== 'ALL' && u.tier !== filterTier) return false
    if (filterStatus === 'ACTIVE' && !u.active) return false
    if (filterStatus === 'INACTIVE' && u.active) return false
    return true
  })

  const toggleActive = (id: string) =>
    setUsers(prev => prev.map(u => u.id === id ? { ...u, active: !u.active } : u))

  const handleCreate = (data: Partial<PlatformUser>) => {
    setUsers(prev => [...prev, {
      id: `u_${Date.now()}`, email: data.email ?? '', password: 'Temp1234!', tier: data.tier ?? 'B_PC',
      fullName: data.fullName ?? '', jobTitle: data.jobTitle ?? '', employeeId: null, assignedJobs: data.tier === 'B_PC' ? [] : null,
      active: true, mfaEnabled: data.mfaEnabled ?? true, mfaMethod: null,
      lastLoginAt: null, lastLoginDevice: null, lastLoginIp: null,
      createdAt: new Date().toISOString(), createdById: 'u1', passwordChangedAt: new Date().toISOString(),
      failedAttempts: 0, lockedUntil: null,
      isSubcontractor: false, subcontractorExpiry: null, subcontractorJobs: null,
      featureOverrides: {}, notes: (data as any).notes ?? null,
    } as PlatformUser])
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          <select className="input text-sm w-auto" value={filterTier} onChange={e => setFilterTier(e.target.value)}>
            <option value="ALL">All tiers</option>
            {Object.entries(TIER_META).map(([t, m]) => <option key={t} value={t}>{m.short}</option>)}
          </select>
          <select className="input text-sm w-auto" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active only</option>
            <option value="INACTIVE">Inactive only</option>
          </select>
        </div>
        {isAdmin && (
          <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" />Create account
          </button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 bg-gray-50 border-b">
                <th className="text-left px-4 py-3 font-medium">User</th>
                <th className="text-left px-3 py-3 font-medium">Tier</th>
                <th className="text-left px-3 py-3 font-medium">MFA</th>
                <th className="text-left px-3 py-3 font-medium">Sessions</th>
                <th className="text-left px-3 py-3 font-medium">Last login</th>
                <th className="text-left px-3 py-3 font-medium">Status</th>
                {isAdmin && <th className="text-right px-4 py-3 font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(u => {
                const sessions = sessionCounts[u.id] ?? 0
                const tierMeta = TIER_META[u.tier]
                return (
                  <tr key={u.id} className={`hover:bg-gray-50 ${!u.active ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${tierMeta?.bg} ${tierMeta?.text}`}>
                          {initialsFrom(u.fullName)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900">{u.fullName}</div>
                          <div className="text-xs text-gray-400 truncate">{u.email}</div>
                          {u.jobTitle && <div className="text-xs text-gray-400">{u.jobTitle}</div>}
                          {u.isSubcontractor && (
                            <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">
                              Subcontractor · expires {u.subcontractorExpiry ? formatDate(u.subcontractorExpiry) : '—'}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3"><TierBadge tier={u.tier} /></td>
                    <td className="px-3 py-3">
                      {u.mfaEnabled
                        ? <span className="flex items-center gap-1 text-xs text-green-700"><CheckCircle className="w-3.5 h-3.5" />{u.mfaMethod ?? 'Enrolled'}</span>
                        : <span className="flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="w-3.5 h-3.5" />Not set up</span>
                      }
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1 text-xs">
                        <div className={`w-2 h-2 rounded-full ${sessions > 0 ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className={sessions > 0 ? 'text-green-700 font-medium' : 'text-gray-400'}>
                          {sessions > 0 ? `${sessions} active` : 'Offline'}
                        </span>
                        {sessions > 0 && (
                          <span className="text-gray-400">/ max {TIER_META[u.tier]?.maxSessions}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500">
                      {u.lastLoginAt
                        ? <div>
                            <div>{new Date(u.lastLoginAt).toLocaleDateString('en-AU',{day:'numeric',month:'short'})}</div>
                            <div className="text-gray-400 truncate max-w-[10rem]">{u.lastLoginDevice}</div>
                          </div>
                        : <span className="text-gray-300">Never logged in</span>
                      }
                    </td>
                    <td className="px-3 py-3"><StatusBadge active={u.active} lockedUntil={u.lockedUntil} /></td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit">
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                          <button className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg" title="Reset password">
                            <Key className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => toggleActive(u.id)}
                            className={`p-1.5 rounded-lg ${u.active ? 'text-gray-400 hover:text-red-600 hover:bg-red-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`}
                            title={u.active ? 'Deactivate' : 'Reactivate'}>
                            {u.active ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 text-xs text-gray-500">
          {filtered.length} user{filtered.length !== 1 ? 's' : ''}
          {' · '}
          {users.filter(u => u.active).length} active
          {' · '}
          {MOCK_ACTIVE_SESSIONS.length} concurrent session{MOCK_ACTIVE_SESSIONS.length !== 1 ? 's' : ''}
        </div>
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onSave={handleCreate} />}
    </div>
  )
}

// ── JOB ASSIGNMENT TAB (PM assigns jobs to Project Coordinators) ───
function JobAssignmentTab() {
  const [users, setUsers]      = useState(MOCK_USERS_REGISTRY)
  const coordinators           = users.filter(u => u.tier === 'B_PC' && u.active)
  const unassignedJobs         = MOCK_JOBS.filter(j =>
    !['COMPLETED','CLOSED'].includes(j.status) &&
    !coordinators.some(c => c.assignedJobs?.includes(j.id))
  )

  const assignJob = (userId: string, jobId: string) => {
    setUsers(prev => prev.map(u =>
      u.id === userId
        ? { ...u, assignedJobs: [...(u.assignedJobs ?? []), jobId] }
        : u
    ))
  }

  const unassignJob = (userId: string, jobId: string) => {
    setUsers(prev => prev.map(u =>
      u.id === userId
        ? { ...u, assignedJobs: (u.assignedJobs ?? []).filter(j => j !== jobId) }
        : u
    ))
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        <strong>Job assignment</strong> controls which jobs each Project Coordinator can see and edit.
        A coordinator has <strong>full edit access</strong> to their assigned jobs and
        <strong> read-only access</strong> on jobs assigned to others. Project Managers see all jobs by default.
      </div>

      {/* Unassigned jobs warning */}
      {unassignedJobs.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{unassignedJobs.length} active job{unassignedJobs.length !== 1 ? 's' : ''} not yet assigned to a coordinator:</span>
          <div className="flex gap-1.5">
            {unassignedJobs.map(j => (
              <span key={j.id} className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full text-xs font-medium">{j.jobNumber}</span>
            ))}
          </div>
        </div>
      )}

      {/* Coordinator cards */}
      <div className="space-y-4">
        {coordinators.map(pc => {
          const pcJobs = MOCK_JOBS.filter(j => pc.assignedJobs?.includes(j.id))
          const availToAssign = MOCK_JOBS.filter(j =>
            !['COMPLETED','CLOSED'].includes(j.status) &&
            !pc.assignedJobs?.includes(j.id) &&
            !coordinators.filter(c => c.id !== pc.id).some(c => c.assignedJobs?.includes(j.id))
          )
          const tierMeta = TIER_META[pc.tier]

          return (
            <div key={pc.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b bg-sky-50/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${tierMeta?.bg} ${tierMeta?.text}`}>
                    {initialsFrom(pc.fullName)}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{pc.fullName}</div>
                    <div className="text-xs text-gray-500">{pc.jobTitle} · {pc.email}</div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {pcJobs.length} job{pcJobs.length !== 1 ? 's' : ''} assigned
                </div>
              </div>

              <div className="p-4 flex flex-wrap gap-2">
                {/* Assigned jobs */}
                {pcJobs.map(job => {
                  const meta = PROJECT_TYPE_META[job.type] ?? PROJECT_TYPE_META.SIGNALS
                  return (
                    <div key={job.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${meta.bg} ${meta.color} ${meta.bg.replace('bg-','border-')}`}>
                      <span className="text-xs font-semibold">{job.jobNumber}</span>
                      <span className="text-xs opacity-75 max-w-[12rem] truncate hidden sm:block">{job.description.split('—')[0].trim()}</span>
                      <button onClick={() => unassignJob(pc.id, job.id)}
                        className="text-gray-400 hover:text-red-500 ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )
                })}

                {/* Add job dropdown */}
                {availToAssign.length > 0 && (
                  <div className="relative">
                    <select
                      className="text-xs border border-dashed border-gray-300 rounded-full px-3 py-1.5 text-gray-500 bg-white hover:bg-gray-50 cursor-pointer appearance-none pr-6"
                      value=""
                      onChange={e => { if (e.target.value) assignJob(pc.id, e.target.value) }}>
                      <option value="">+ Assign job…</option>
                      {availToAssign.map(j => (
                        <option key={j.id} value={j.id}>{j.jobNumber} — {j.description.split('—')[0].trim()}</option>
                      ))}
                    </select>
                  </div>
                )}

                {pcJobs.length === 0 && (
                  <div className="text-xs text-gray-400 italic">No jobs assigned yet — use the dropdown to assign</div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Project Managers (for reference) */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50">
          <h3 className="font-semibold text-sm text-gray-900">Project Managers (full access — no scoping)</h3>
        </div>
        {users.filter(u => u.tier === 'B_PM' && u.active).map(pm => (
          <div key={pm.id} className="px-5 py-3 flex items-center gap-3 border-b last:border-0">
            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">{initialsFrom(pm.fullName)}</div>
            <div>
              <div className="text-sm font-medium text-gray-900">{pm.fullName}</div>
              <div className="text-xs text-gray-500">{pm.jobTitle} · Sees all {MOCK_JOBS.length} jobs</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── FEATURE MATRIX TAB ─────────────────────────────────────────────
function TierConfigTab() {
  const categories = [...new Set(Object.values(FEATURE_ACCESS_CONFIG).map(f => f.category))]
  const TIERS = ['A_DEVELOPER','B_PM','B_PC','C_PROCUREMENT','D_FIELD']
  const ACCESS_STYLE: Record<string, { bg: string; text: string; label: string }> = {
    FULL:    { bg: 'bg-green-100',  text: 'text-green-700', label: 'Full' },
    LIMITED: { bg: 'bg-amber-100',  text: 'text-amber-700', label: 'Ltd' },
    NONE:    { bg: 'bg-gray-100',   text: 'text-gray-400',  label: '—' },
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Default feature access per tier. Adjustable features can be overridden per-user.
        Core access rules cannot be changed without a code deployment.
      </p>
      {categories.map(cat => {
        const features = Object.entries(FEATURE_ACCESS_CONFIG).filter(([,f]) => f.category === cat)
        return (
          <div key={cat} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-2.5 border-b bg-gray-50">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">{cat}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b">
                    <th className="text-left px-4 py-2 font-medium w-64">Feature</th>
                    {TIERS.map(t => (
                      <th key={t} className="text-center px-3 py-2 font-medium">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${TIER_META[t]?.bg} ${TIER_META[t]?.text}`}>
                          {TIER_META[t]?.short}
                        </span>
                      </th>
                    ))}
                    <th className="text-center px-3 py-2 font-medium text-gray-400">Adjustable</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {features.map(([key, f]) => (
                    <tr key={key} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-sm text-gray-700">{f.label}</td>
                      {TIERS.map(t => {
                        const access = f.defaults[t] ?? 'NONE'
                        const s = ACCESS_STYLE[access]
                        return (
                          <td key={t} className="px-3 py-2.5 text-center">
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${s.bg} ${s.text}`}>{s.label}</span>
                          </td>
                        )
                      })}
                      <td className="px-3 py-2.5 text-center">
                        {f.adjustable
                          ? <CheckCircle className="w-3.5 h-3.5 text-green-500 mx-auto" />
                          : <Lock className="w-3.5 h-3.5 text-gray-300 mx-auto" />
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── ACTIVE SESSIONS TAB ────────────────────────────────────────────
function ActiveSessionsTab() {
  const [sessions, setSessions] = useState(MOCK_ACTIVE_SESSIONS)

  const revoke = (id: string) => setSessions(prev => prev.filter(s => s.id !== id))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          <strong>{sessions.length}</strong> concurrent session{sessions.length !== 1 ? 's' : ''} across <strong>{new Set(sessions.map(s => s.userId)).size}</strong> users
        </p>
        <button className="text-xs text-red-600 hover:underline flex items-center gap-1">
          <LogOut className="w-3.5 h-3.5" />Revoke all non-admin sessions
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
        {sessions.map(s => {
          const tm = TIER_META[s.tier]
          const minsAgo = Math.floor((Date.now() - new Date(s.lastActiveAt).getTime()) / 60000)
          return (
            <div key={s.id} className={`px-5 py-3.5 flex items-center gap-4 ${s.current ? 'bg-blue-50/30' : 'hover:bg-gray-50'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${tm?.bg} ${tm?.text}`}>
                {initialsFrom(s.fullName)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-900">{s.fullName}</span>
                  <TierBadge tier={s.tier} />
                  {s.current && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">Your session</span>}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-3">
                  <span className="flex items-center gap-1"><Monitor className="w-3 h-3" />{s.device}</span>
                  <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{s.ip}</span>
                  <span className="flex items-center gap-1">
                    <Activity className="w-3 h-3" />
                    {minsAgo <= 1 ? 'Active now' : `${minsAgo}m ago`}
                  </span>
                </div>
              </div>
              <div className="text-xs text-gray-400 text-right flex-shrink-0">
                <div>Since {new Date(s.loginAt).toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit'})}</div>
                <div>{new Date(s.loginAt).toLocaleDateString('en-AU',{day:'numeric',month:'short'})}</div>
              </div>
              {!s.current && (
                <button onClick={() => revoke(s.id)}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0">
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )
        })}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs text-gray-500">
        <strong>Session limits per spec:</strong> Tier A max 2 · Tier B/C max 3 · Tier D max 1 device.
        Inactivity timeout: Tier A/B 60 min · Tier C 120 min · Tier D 24 hr.
        Password change immediately forces logout from all active sessions.
      </div>
    </div>
  )
}

// ── AUDIT LOG TAB ──────────────────────────────────────────────────
function AuditLogTab() {
  const [catFilter, setCatFilter] = useState('ALL')
  const [search, setSearch]       = useState('')
  const [events]                  = useState<AuditEvent[]>(MOCK_AUDIT_LOG)

  const filtered = events.filter(e => {
    if (catFilter !== 'ALL' && e.category !== catFilter) return false
    if (search) {
      const q = search.toLowerCase()
      return e.email.includes(q) || e.detail.toLowerCase().includes(q) || e.event.toLowerCase().includes(q)
    }
    return true
  })

  const CAT_STYLE: Record<string, string> = {
    AUTH:   'bg-blue-100 text-blue-700',
    DATA:   'bg-green-100 text-green-700',
    ACCESS: 'bg-gray-100 text-gray-600',
    ADMIN:  'bg-purple-100 text-purple-700',
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <input className="input pl-9 text-sm" placeholder="Search user, event, detail…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1.5">
          {['ALL','AUTH','DATA','ADMIN','ACCESS'].map(c => (
            <button key={c} onClick={() => setCatFilter(c)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${catFilter === c ? 'bg-brand-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {c === 'ALL' ? 'All' : c}
            </button>
          ))}
        </div>
        <button className="btn-secondary text-sm flex items-center gap-1.5">
          <FileText className="w-4 h-4" />Export
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="divide-y divide-gray-100">
          {filtered.map(ev => {
            const em = AUDIT_EVENT_META[ev.event] ?? { label: ev.event, bg: 'bg-gray-100', text: 'text-gray-600' }
            const tm = ev.tier ? TIER_META[ev.tier] : null
            return (
              <div key={ev.id} className="px-5 py-3 flex items-start gap-4 hover:bg-gray-50">
                <div className="flex-shrink-0 text-xs text-gray-400 w-28 pt-0.5">
                  <div>{new Date(ev.timestamp).toLocaleDateString('en-AU',{day:'numeric',month:'short'})}</div>
                  <div>{new Date(ev.timestamp).toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit'})}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${em.bg} ${em.text}`}>{em.label}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${CAT_STYLE[ev.category] ?? ''}`}>{ev.category}</span>
                    {tm && <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${tm.bg} ${tm.text}`}>{tm.short}</span>}
                  </div>
                  <div className="text-sm font-medium text-gray-900">{ev.email}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{ev.detail}</div>
                </div>
                <div className="text-xs text-gray-400 flex-shrink-0">{ev.ip}</div>
              </div>
            )
          })}
          {filtered.length === 0 && (
            <div className="py-12 text-center text-gray-400 text-sm">No events match your filters</div>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 text-xs text-gray-500">
          Showing {filtered.length} of {events.length} audit events · All access-related events are logged and retained for 12 months
        </div>
      </div>
    </div>
  )
}

// ── SECURITY SETTINGS TAB ──────────────────────────────────────────
function SecurityTab() {
  const [thresholds, setThresholds] = useState(PO_THRESHOLDS)
  const fmt = (n: number) => `$${n.toLocaleString()}`

  return (
    <div className="space-y-6">
      {/* PO Thresholds */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b bg-gray-50"><h3 className="font-semibold text-gray-900">PO Approval Thresholds</h3></div>
        <div className="p-5 space-y-4">
          {[
            { key: 'tierC_createLimit',   label: 'Tier C create limit',         sub: 'POs above this created by Tier C auto-route to Tier B for approval' },
            { key: 'tierB_approveLimit',  label: 'Tier B approval limit',       sub: 'POs above this escalate to Tier A for approval' },
            { key: 'requireDualApproval', label: 'Dual approval threshold',     sub: 'Requires two Tier B signatures above this value' },
            { key: 'energyGrid_tierB',    label: 'Energy Grid Tier B limit',    sub: 'Tighter limit for HV / Energex work (overrides standard Tier B limit)' },
          ].map(({ key, label, sub }) => (
            <div key={key} className="flex items-center gap-4">
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">{label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{sub}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">$</span>
                <input type="number" step="1000"
                  value={thresholds[key as keyof typeof thresholds]}
                  onChange={e => setThresholds(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                  className="input w-28 text-right text-sm font-medium"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Session & Password policy */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b bg-gray-50"><h3 className="font-semibold text-gray-900">Session & Password Policy</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 bg-gray-50 border-b">
                <th className="text-left px-4 py-2.5 font-medium">Setting</th>
                {Object.entries(TIER_META).map(([t, m]) => (
                  <th key={t} className="text-center px-3 py-2.5 font-medium">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${m.bg} ${m.text}`}>{m.short}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {[
                { label: 'Max concurrent sessions', values: Object.values(TIER_META).map(m => String(m.maxSessions)) },
                { label: 'Inactivity timeout',      values: Object.values(TIER_META).map(m => m.inactivityMins >= 60 ? `${m.inactivityMins/60}h` : `${m.inactivityMins}m`) },
                { label: 'MFA required',             values: Object.values(TIER_META).map(m => m.mfaRequired ? 'Yes' : 'Optional') },
                { label: 'Min password length',      values: Object.values(TIER_META).map(m => `${m.passwordMinLength} chars`) },
                { label: 'Password expiry',          values: Object.values(TIER_META).map(m => m.passwordExpireDays ? `${m.passwordExpireDays} days` : 'No expiry') },
              ].map(row => (
                <tr key={row.label} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-700">{row.label}</td>
                  {row.values.map((v, i) => (
                    <td key={i} className="px-3 py-2.5 text-center text-xs font-medium text-gray-700">{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 text-xs text-gray-500">
          Policy changes apply immediately to all new sessions. Existing sessions are not affected until re-login.
        </div>
      </div>
    </div>
  )
}

// ── MAIN SETTINGS PAGE ────────────────────────────────────────────
export function SettingsPage() {
  const { user, isTier, canAssignJobs } = useAuthStore()
  const [tab, setTab] = useState<'users'|'jobs'|'tiers'|'sessions'|'security'|'audit'|'currency'|'payrates'|'beta'>('users')

  if (!user) return null

  const isAdmin    = isTier('A_DEVELOPER')
  const isPM       = canAssignJobs()

  // Non-admin, non-PM → show just job assignment
  if (!isAdmin && !isPM) {
    return (
      <div className="space-y-5">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <div className="bg-white border border-gray-200 rounded-xl py-14 text-center text-gray-400">
          <Lock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">Platform settings are restricted to Tier A administrators.</p>
        </div>
      </div>
    )
  }

  const TABS = [
    { key: 'users',    icon: Users,    label: 'User accounts',    show: isAdmin },
    { key: 'jobs',     icon: Briefcase,label: 'Job assignment',   show: isPM },
    { key: 'tiers',    icon: Shield,   label: 'Tier config',      show: isAdmin },
    { key: 'sessions', icon: Monitor,  label: 'Active sessions',  show: isAdmin },
    { key: 'security', icon: Lock,     label: 'Security & thresholds', show: isAdmin },
    { key: 'audit',    icon: FileText, label: 'Audit log',        show: isAdmin },
    { key: 'currency' as const, icon: Globe,       label: 'Currency',    show: true },
    { key: 'payrates' as const,  icon: Calculator,   label: 'Pay & Rates', show: isAdmin || isPM },
    { key: 'beta'     as const,  icon: AlertTriangle, label: 'Beta / Test Mode', show: isAdmin },
  ].filter(t => t.show) as { key: typeof tab; icon: any; label: string; show: boolean }[]

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings &amp; Access Control</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {isAdmin ? 'Tier A — full platform administration' : 'Project Manager — job assignment only'}
        </p>
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 flex-wrap">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {tab === 'users'    && <UserAccountsTab isAdmin={isAdmin} />}
      {tab === 'jobs'     && <JobAssignmentTab />}
      {tab === 'tiers'    && <TierConfigTab />}
      {tab === 'sessions' && <ActiveSessionsTab />}
      {tab === 'security' && <SecurityTab />}
      {tab === 'audit'    && <AuditLogTab />}
        {tab === 'currency'  && <CurrencyTab isAdmin={isAdmin} />}
        {tab === 'payrates'  && <PayRatesTab  isAdmin={isAdmin} />}
        {tab === 'beta'      && <BetaModeTab  isAdmin={isAdmin} user={user} />}
    </div>
  )
}
// ─────────────────────────────────────────────────────────────────
// PAY & RATES TAB
// ─────────────────────────────────────────────────────────────────
// Single source of truth for all pay rates in the platform.
// Replaces the static PAY_RATES constant in mockDataExtended.ts.
//
// How apprentice rates work:
//   effectiveRate = max( C10BaseRate × yearPercentage[year], adultNMW )
//   where:
//     year           = floor((today − commencementDate) / 365.25) + 1 (capped at 4)
//     adultNMW       = only applied if employee was ≥21 at commencement
//
// Admin updates C10 and NMW each 1 July. All apprentice rates
// recalculate instantly — no code change required.
// ─────────────────────────────────────────────────────────────────

// Module-level pay rate store (replaces static PAY_RATES)
// In production this would be persisted to database.
interface PayRateStore {
  effectiveDate:       string
  updatedByName:       string
  updatedAt:           string
  // Base references
  c10BaseRate:         number   // Electrical Trade Worker Grade 1 ordinary hourly rate
  nationalMinWage:     number   // Fair Work national minimum wage (adult)
  // Apprentice year percentages (% of C10) — Award defaults, EBA-overridable
  apprenticePct: {
    year1: number; year2: number; year3: number; year4: number
  }
  // Classification rates (ordinary hourly)
  rates: Record<string, number>
  // Night shift loading % applied to ordinary night hours
  nightShiftLoadingPct: number
  // Overtime thresholds and multipliers (per standard working day)
  overtimeThreshold:  number   // hours before OT kicks in (default 8)
  otMultiplier:       number   // OT rate multiplier (default 1.5)
  doubleTimeAfter:    number   // hours before double-time kicks in (default 10)
  dtMultiplier:       number   // double-time multiplier (default 2.0)
  // Weekend penalty rates
  satFirstHoursOt:    number   // Saturday — first N hours at OT rate (default 2)
  satMultiplier:      number   // Saturday OT multiplier (default 1.5)
  satDtMultiplier:    number   // Saturday DT multiplier (default 2.0)
  sunMultiplier:      number   // Sunday — all hours (default 2.0)
  // Allowances (per week unless marked per-hour)
  allowances: {
    toolAllowance:         number
    firstAidOfficer:       number
    leadingHand:           number
    heightWork:            number   // per hour, >6m
    confinedSpace:         number   // per hour
  }
  // Weekly hours configuration (F3)
  weeklyOrdinaryHours: number
  weeklyHoursCap:      number
  // Per-classification daily OT thresholds (P3)
  classificationOTThresholds: Record<string, number>
  // Industry levies (% of ordinary wages — employer obligation)
  cipqLevyPct:     number   // Portable long service leave
  bewtLevyPct:     number   // Building & Engineering Workers Training
}

const DEFAULT_RATES: PayRateStore = {
  effectiveDate:       '2026-07-01',
  updatedByName:       'System default',
  updatedAt:           new Date().toISOString(),
  c10BaseRate:         43.00,
  nationalMinWage:     24.10,
  apprenticePct:       { year1: 35, year2: 55, year3: 75, year4: 90 },
  rates: {
    ELECTRICAL_A_GRADE:       43.00,
    ELECTRICAL_APPRENTICE_1:  20.50,   // provisional — will be calculated from c10 × pct
    ELECTRICAL_APPRENTICE_2:  24.00,
    ELECTRICAL_APPRENTICE_3:  29.00,
    ELECTRICAL_APPRENTICE_4:  34.50,
    TRAFFIC_CONTROLLER:       35.50,
    CIVIL_LABOURER:           32.00,
    LEADING_HAND_ALLOWANCE:    2.50,
    SITE_MANAGER:             58.00,
    PROJECT_MANAGER:          68.00,
    ADMINISTRATION:           35.00,
  },
  nightShiftLoadingPct: 15,
  overtimeThreshold:  8,
  otMultiplier:       1.5,
  doubleTimeAfter:    10,
  dtMultiplier:       2.0,
  satFirstHoursOt:    2,
  satMultiplier:      1.5,
  satDtMultiplier:    2.0,
  sunMultiplier:      2.0,
  allowances: {
    toolAllowance:     18.35,
    firstAidOfficer:   16.60,
    leadingHand:       27.50,
    heightWork:         0.95,
    confinedSpace:      1.10,
  },
  cipqLevyPct:   2.70,
  bewtLevyPct:   0.10,
  weeklyOrdinaryHours: 38,
  weeklyHoursCap:      72,
  classificationOTThresholds: {
    ELECTRICAL_A_GRADE: 8.0, ELECTRICAL_APPRENTICE_1: 7.6, ELECTRICAL_APPRENTICE_2: 7.6,
    ELECTRICAL_APPRENTICE_3: 7.6, ELECTRICAL_APPRENTICE_4: 7.6, TRAFFIC_CONTROLLER: 8.0,
    CIVIL_LABOURER: 8.0, ADMINISTRATION: 7.6,
  },
}

// Module-level store — read by Timesheets, Roster payroll tab, etc.
let _payRates: PayRateStore = { ...DEFAULT_RATES }
const _payRateHistory: PayRateStore[] = []

export function getPayRates(): PayRateStore { return { ..._payRates } }

// ─────────────────────────────────────────────────────────────────
// PHASE 4 — BETA / TEST MODE TAB
// Test mode toggle + TS01–TS15 beta sign-off checklist
// ─────────────────────────────────────────────────────────────────

const BETA_SCENARIOS = [
  { id:'TS01', tier:'Tier B — PM',    title:'Create a job from a won tender',              desc:'Job auto-created, budget pre-populated from estimate, milestone schedule imported from contract.' },
  { id:'TS02', tier:'Tier C',          title:'Raise PO, receive goods (GRN), confirm receipt', desc:'GRN confirmed, BOM stock status updates to ON_SITE, Dashboard spend figure updates.' },
  { id:'TS03', tier:'Tier B — PM',    title:'Build day roster, push to crew mobile',        desc:'Crew receive push notification, confirm attendance, status shows CONFIRMED in Live Crew tab.' },
  { id:'TS04', tier:'Tier B — PM',    title:'Night-shift crew isolated from day view',      desc:'Night crew (Sun–Thu) only in Night tab. Day crew (Mon–Fri) only in Day tab. No cross-contamination.' },
  { id:'TS05', tier:'B PM + D Field',  title:'Submit timesheet, export to payroll',          desc:'Hours match roster. PM approves. ABA file generated with correct hours, PAYG and super amounts.' },
  { id:'TS06', tier:'Tier B — PM',    title:'Approve progress claim, push to Xero',        desc:'Claim approved, invoice appears in Xero sandbox with correct line items and GST.' },
  { id:'TS07', tier:'B PC + D Field',  title:'Create SWMS, record toolbox attendance',       desc:'SWMS approved, Toolbox checklist generated, attendance recorded with digital sign-off per worker.' },
  { id:'TS08', tier:'Tier D — Field', title:'Log CRITICAL incident, trigger corrective action', desc:'Incident logged, PM notified, SWMS flagged for review, corrective action assigned in WHS.' },
  { id:'TS09', tier:'D Field + B PM',  title:'Complete work instructions, sign off ITP hold point', desc:'WI steps completed, ITP hold point marked as witnessed, job shows Handover readiness flag.' },
  { id:'TS10', tier:'Tier B — PM',    title:'Complete handover, auto-file to Archive Centre', desc:'Handover pack completed, documents filed to Archive under correct project, searchable.' },
  { id:'TS11', tier:'Tier B — PM',    title:'Recall entire site crew — safety stop-work', desc:'All ON_SITE crew receive recall notification, status updates to RECALLED, audit log entry created.' },
  { id:'TS12', tier:'Tier B — PM',    title:'Allocate plant — OPEN defect blocks allocation', desc:'Asset with OPEN defect cannot be allocated to any job until defect is closed.' },
  { id:'TS13', tier:'Tier A — Dev',   title:'Settings branding: upload logo, generate PO PDF', desc:'Logo in PO header, brand colour, ABN and document reference in footer.' },
  { id:'TS14', tier:'Tier D — Field', title:'Tier-gating: D_FIELD cannot access commercial pages', desc:'Estimating, Contracts, Spend Analytics, Xero show locked/redirect. No bypass.' },
  { id:'TS15', tier:'Tier A + B',      title:'Session timeout and MFA re-authentication',    desc:'After 60 min inactivity session expires; user must re-authenticate with MFA to continue.' },
]

function BetaModeTab({ isAdmin, user }: { isAdmin: boolean; user: any }) {
  const [testModeOn, setTestModeOn] = useState(isTestMode())
  const [checks, setChecks] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('tp_beta_checklist') ?? '{}') } catch { return {} }
  })

  const toggleCheck = (id: string) => {
    const next = { ...checks, [id]: !checks[id] }
    setChecks(next)
    try { localStorage.setItem('tp_beta_checklist', JSON.stringify(next)) } catch {}
  }

  const handleToggleTestMode = () => {
    if (testModeOn) { disableTestMode(); setTestModeOn(false) }
    else            { enableTestMode(user?.fullName ?? 'Admin'); setTestModeOn(true) }
  }

  const passed   = Object.values(checks).filter(Boolean).length
  const total    = BETA_SCENARIOS.length
  const allPassed = passed === total
  const pct      = Math.round((passed / total) * 100)

  return (
    <div className="space-y-6">

      {/* Test mode toggle */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50">
          <h2 className="font-semibold text-gray-900">Platform mode</h2>
          <p className="text-xs text-gray-500 mt-0.5">Toggle Test Mode to label the platform BETA and show a warning banner on every page.</p>
        </div>
        <div className="px-6 py-5 flex items-center justify-between gap-4">
          <div>
            <div className={`font-semibold mb-0.5 ${testModeOn ? 'text-amber-700' : 'text-gray-900'}`}>
              {testModeOn ? 'BETA TEST MODE — active' : 'Prototype mode — active'}
            </div>
            <p className="text-xs text-gray-500">
              {testModeOn
                ? 'Amber BETA banner shown on all pages. Disable before full production go-live.'
                : 'Enable to show the BETA banner and signal this is a test environment.'}
            </p>
          </div>
          <button
            disabled={!isAdmin}
            onClick={handleToggleTestMode}
            className={'relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ' + (testModeOn ? 'bg-amber-500' : 'bg-gray-300') + (!isAdmin ? ' opacity-40 cursor-not-allowed' : ' cursor-pointer')}
          >
            <span className={'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ' + (testModeOn ? 'translate-x-6' : '')} />
          </button>
        </div>
        {testModeOn && (
          <div className="px-6 pb-5">
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800">
              <strong>BETA TEST MODE is active.</strong> The amber banner is visible on all pages.
              Do not run live payroll or send client invoices until all 15 beta scenarios are signed off
              and Test Mode is disabled.
            </div>
          </div>
        )}
      </div>

      {/* Sign-off checklist */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">Beta sign-off checklist — TS01 to TS15</h2>
            <p className="text-xs text-gray-500 mt-0.5">All 15 scenarios must pass before the platform is declared Beta Ready.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className={'text-2xl font-bold ' + (allPassed ? 'text-green-700' : passed > 0 ? 'text-amber-600' : 'text-gray-400')}>{passed}/{total}</div>
              <div className="text-xs text-gray-400">{pct}% complete</div>
            </div>
            <div className="w-16 h-16 flex-shrink-0">
              <svg viewBox="0 0 36 36" className="-rotate-90 w-full h-full">
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="#f3f4f6" strokeWidth="3" />
                <circle cx="18" cy="18" r="15.5" fill="none"
                  stroke={allPassed ? '#22c55e' : passed > 0 ? '#f59e0b' : '#d1d5db'}
                  strokeWidth="3"
                  strokeDasharray={`${(passed / total) * 97.4} 97.4`}
                  strokeLinecap="round" />
              </svg>
            </div>
          </div>
        </div>

        {allPassed && (
          <div className="mx-6 mt-4 bg-green-50 border border-green-300 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-green-800">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div>
              <span className="font-bold">All 15 scenarios passed.</span>{' '}
              The platform is eligible for Beta sign-off. Disable Test Mode and transition to production
              once the Xero sandbox dry-run and STP test event are complete.
            </div>
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {BETA_SCENARIOS.map(s => (
            <label key={s.id} className={'flex items-start gap-4 px-6 py-4 cursor-pointer hover:bg-gray-50 ' + (checks[s.id] ? 'bg-green-50/50' : '')}>
              <input type="checkbox" checked={!!checks[s.id]} onChange={() => toggleCheck(s.id)}
                className="mt-0.5 w-4 h-4 accent-green-600 rounded flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="text-xs font-mono text-gray-400 flex-shrink-0">{s.id}</span>
                  <span className="text-sm font-semibold text-gray-900">{s.title}</span>
                  {checks[s.id] && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">✓ Passed</span>}
                </div>
                <p className="text-xs text-gray-500">{s.desc}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">Required user tier: {s.tier}</p>
              </div>
            </label>
          ))}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
          <p className="text-xs text-gray-400">Tick each scenario after end-to-end testing at the indicated tier with real user accounts.</p>
          {passed > 0 && (
            <button onClick={() => {
              setChecks({})
              try { localStorage.setItem('tp_beta_checklist', '{}') } catch {}
            }} className="text-xs text-red-500 hover:underline flex-shrink-0 ml-4">Reset checklist</button>
          )}
        </div>
      </div>
    </div>
  )
}

/** Single source of truth for any employee hourly rate.
 * Apprentices: C10 x yearPct with adult NMW floor if applicable.
 * All others: reads from _payRates.rates.
 * Used by Timesheets, Payslips, Payroll CSV and PLSL report.
 */
export function getEffectiveRate(
  payClassification: string,
  isAdultApprentice = false,
): number {
  const pcts = _payRates.apprenticePct
  const c10  = _payRates.c10BaseRate
  const nmw  = _payRates.nationalMinWage
  const yearMap: Record<string, number> = {
    ELECTRICAL_APPRENTICE_1: pcts.year1,
    ELECTRICAL_APPRENTICE_2: pcts.year2,
    ELECTRICAL_APPRENTICE_3: pcts.year3,
    ELECTRICAL_APPRENTICE_4: pcts.year4,
  }
  if (yearMap[payClassification] !== undefined) {
    const yearRate = +(c10 * yearMap[payClassification] / 100).toFixed(2)
    return isAdultApprentice ? Math.max(yearRate, nmw) : yearRate
  }
  return _payRates.rates[payClassification] ?? 0
}

/** Calculate the effective apprentice rate for a given year and adult status */
export function calcApprenticeRate(year: 1|2|3|4, isAdultApprentice: boolean): number {
  const pcts = _payRates.apprenticePct
  const pct  = year === 1 ? pcts.year1 : year === 2 ? pcts.year2 : year === 3 ? pcts.year3 : pcts.year4
  const yearRate = _payRates.c10BaseRate * (pct / 100)
  return isAdultApprentice ? Math.max(yearRate, _payRates.nationalMinWage) : yearRate
}

// ─────────────────────────────────────────────────────────────────
// PAY RATES TAB COMPONENT
// ─────────────────────────────────────────────────────────────────
function PayRatesTab({ isAdmin }: { isAdmin: boolean }) {
  const [store, setStore]         = useState<PayRateStore>(() => getPayRates())
  const [payPeriodSetting, setPayPeriodSetting] = useState(() => getPayPeriod())
  const [saved, setSaved]         = useState(false)
  const [showHistory, setHistory] = useState(false)
  const [history]                 = useState<PayRateStore[]>(_payRateHistory)
  const { user }                  = useAuthStore()

  const handleSave = () => {
    _payRateHistory.push({ ..._payRates })
    _payRates = { ...store, updatedByName: user?.fullName ?? 'Admin', updatedAt: new Date().toISOString() }
    setStore({ ..._payRates })
    // Propagate night shift loading to calcHours() immediately
    setNightShiftLoading(store.nightShiftLoadingPct)
    setPayPeriod(payPeriodSetting)
    setWeeklyHoursCap(store.weeklyHoursCap)
    setClassificationOTThresholds(store.classificationOTThresholds)
    // Recompute apprentice rates from C10 x year percentages.
    // _payRates.rates is updated so getPayRates().rates and
    // getEffectiveRate() both return live values immediately.
    const p4 = store.apprenticePct
    const c4 = store.c10BaseRate
    _payRates.rates.ELECTRICAL_APPRENTICE_1 = +(c4 * p4.year1 / 100).toFixed(2)
    _payRates.rates.ELECTRICAL_APPRENTICE_2 = +(c4 * p4.year2 / 100).toFixed(2)
    _payRates.rates.ELECTRICAL_APPRENTICE_3 = +(c4 * p4.year3 / 100).toFixed(2)
    _payRates.rates.ELECTRICAL_APPRENTICE_4 = +(c4 * p4.year4 / 100).toFixed(2)

    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  // Derived: apprentice rates from C10 × %
  const derivedApprenticeRates = [1,2,3,4].map(yr => {
    const pct    = yr === 1 ? store.apprenticePct.year1 : yr === 2 ? store.apprenticePct.year2 : yr === 3 ? store.apprenticePct.year3 : store.apprenticePct.year4
    const rate   = store.c10BaseRate * (pct / 100)
    const adult  = Math.max(rate, store.nationalMinWage)
    return { yr, pct, rate: rate.toFixed(2), adult: adult.toFixed(2) }
  })

  const Field = ({ label, value, onChange, suffix = '/hr', type = 'number', step = '0.01', prefix = '$', note = '' }: any) => (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-center gap-1.5">
        {prefix && <span className="text-sm text-gray-500 font-medium">{prefix}</span>}
        <input type={type} step={step} min="0" value={value}
          onChange={e => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          disabled={!isAdmin}
          className="input text-sm w-full disabled:bg-gray-50 disabled:text-gray-500" />
        {suffix && <span className="text-xs text-gray-400 flex-shrink-0">{suffix}</span>}
      </div>
      {note && <p className="text-[10px] text-gray-400 mt-0.5">{note}</p>}
    </div>
  )

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-gray-900">Pay & Rates configuration</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Single source of truth for all hourly rates, overtime rules, allowances, and levies.
            Update C10 and the national minimum wage each 1 July when Fair Work Commission announces new rates.
            All apprentice rates recalculate automatically.
          </p>
        </div>
        {!isAdmin && (
          <span className="flex-shrink-0 text-xs bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">
            View only — Tier A to edit
          </span>
        )}
      </div>

      {saved && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-green-800">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          Pay rates saved. All payroll calculations will use the updated values immediately.
        </div>
      )}

      {/* ── SECTION 1: Base rates (annual FWC update) ────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
          <Calculator className="w-4 h-4 text-blue-600" />
          <h3 className="font-semibold text-gray-900 text-sm">Base rates — update each 1 July (Fair Work Commission)</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="C10 — Electrical Trade Worker (Grade 1)"
            value={store.c10BaseRate}
            onChange={(v: number) => setStore(p => ({ ...p, c10BaseRate: v }))}
            note="Drives all four apprentice year rates via % below" />
          <Field label="National minimum wage (adult)"
            value={store.nationalMinWage}
            onChange={(v: number) => setStore(p => ({ ...p, nationalMinWage: v }))}
            note="Floor rate for adult apprentices (≥21 at commencement)" />
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Effective date</label>
            <input type="date" value={store.effectiveDate}
              onChange={e => setStore(p => ({ ...p, effectiveDate: e.target.value }))}
              disabled={!isAdmin}
              className="input text-sm w-full disabled:bg-gray-50" />
            <p className="text-[10px] text-gray-400 mt-0.5">Date from which new rates apply</p>
          </div>
        </div>
      </div>

      {/* ── SECTION 2: Apprentice year percentages + derived rates ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 text-sm">Electrical apprentice year percentages</h3>
          <span className="text-xs text-gray-400">% of C10 base rate · Award defaults shown · override for EBA</span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {([
            { label: '1st year', key: 'year1', award: 35 },
            { label: '2nd year', key: 'year2', award: 55 },
            { label: '3rd year', key: 'year3', award: 75 },
            { label: '4th year', key: 'year4', award: 90 },
          ] as const).map(f => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-gray-700 mb-1">{f.label}</label>
              <div className="flex items-center gap-1">
                <input type="number" step="0.5" min="0" max="100"
                  value={store.apprenticePct[f.key]}
                  onChange={e => setStore(p => ({ ...p, apprenticePct: { ...p.apprenticePct, [f.key]: parseFloat(e.target.value)||0 }}))}
                  disabled={!isAdmin}
                  className="input text-sm w-full disabled:bg-gray-50" />
                <span className="text-xs text-gray-400 flex-shrink-0">%</span>
              </div>
              <p className="text-[10px] text-gray-400 mt-0.5">Award: {f.award}%</p>
            </div>
          ))}
        </div>

        {/* Derived rate table */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-blue-100 border-b border-blue-200">
            <p className="text-xs font-semibold text-blue-800">Derived apprentice rates (calculated automatically)</p>
            <p className="text-[10px] text-blue-600 mt-0.5">
              Junior rate = C10 × %. Adult rate = max(C10 × %, national min wage).
              Applies when employee was ≥21 at apprenticeship commencement.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] text-blue-700 border-b border-blue-200">
                  <th className="text-left px-4 py-2 font-semibold">Year</th>
                  <th className="text-center px-3 py-2 font-semibold">% of C10</th>
                  <th className="text-right px-3 py-2 font-semibold">Junior rate /hr</th>
                  <th className="text-right px-3 py-2 font-semibold">Adult rate /hr (≥21)</th>
                  <th className="text-left px-3 py-2 font-semibold">Applied via</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-100">
                {derivedApprenticeRates.map(r => (
                  <tr key={r.yr} className="hover:bg-blue-50/60">
                    <td className="px-4 py-2 font-medium text-gray-900">{r.yr === 1 ? '1st' : r.yr === 2 ? '2nd' : r.yr === 3 ? '3rd' : '4th'} year</td>
                    <td className="px-3 py-2 text-center text-gray-700">{r.pct}%</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900">${r.rate}</td>
                    <td className="px-3 py-2 text-right font-semibold text-blue-700">${r.adult}</td>
                    <td className="px-3 py-2 text-gray-500">apprenticeYear on employee record</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-blue-50 border-t border-blue-200">
            <p className="text-[10px] text-blue-700">
              ⓘ  The system calculates apprentice year automatically from <strong>apprentice commencement date</strong> on the employee record.
              Rate updates here apply immediately to all future payroll calculations. Historical timesheets retain the rate that was active when they were approved.
            </p>
          </div>
        </div>
      </div>

      {/* ── SECTION 3: All classification rates ─────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 text-sm">All pay classifications — ordinary hourly rate</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {([
            { key: 'ELECTRICAL_A_GRADE',     label: 'Electrical A Grade (C10)' },
            { key: 'TRAFFIC_CONTROLLER',     label: 'Traffic Controller' },
            { key: 'CIVIL_LABOURER',         label: 'Civil Labourer' },
            { key: 'SITE_MANAGER',           label: 'Site Manager' },
            { key: 'PROJECT_MANAGER',        label: 'Project Manager' },
            { key: 'ADMINISTRATION',         label: 'Administration' },
          ]).map(c => (
            <Field key={c.key} label={c.label}
              value={store.rates[c.key] ?? 0}
              onChange={(v: number) => setStore(p => ({ ...p, rates: { ...p.rates, [c.key]: v }}))} />
          ))}
        </div>
        <p className="text-xs text-gray-400">
          Apprentice rates above are calculated from C10 × year percentage — they do not need to be entered here separately.
        </p>
      </div>

      {/* ── SECTION 4: Penalty rates and loadings ───────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 text-sm">Penalty rates, loadings, and overtime rules</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="Night shift loading" value={store.nightShiftLoadingPct} suffix="% of ord. rate"
            prefix="" step="0.5"
            note="Applied to all ordinary hours worked during night shift window"
            onChange={(v: number) => setStore(p => ({ ...p, nightShiftLoadingPct: v }))} />
          <Field label="Overtime threshold (daily)" value={store.overtimeThreshold} suffix="hrs/day"
            prefix="" step="0.5"
            note="Hours above this → OT rate"
            onChange={(v: number) => setStore(p => ({ ...p, overtimeThreshold: v }))} />
          <Field label="OT multiplier" value={store.otMultiplier} prefix="" suffix="× ord. rate" step="0.25"
            note="Applied from threshold to double-time threshold"
            onChange={(v: number) => setStore(p => ({ ...p, otMultiplier: v }))} />
          <Field label="Double time after" value={store.doubleTimeAfter} suffix="hrs/day"
            prefix="" step="0.5"
            note="Hours above this → double time rate"
            onChange={(v: number) => setStore(p => ({ ...p, doubleTimeAfter: v }))} />
          <Field label="DT multiplier" value={store.dtMultiplier} prefix="" suffix="× ord. rate" step="0.25"
            onChange={(v: number) => setStore(p => ({ ...p, dtMultiplier: v }))} />
          <Field label="Saturday — first N hours at OT" value={store.satFirstHoursOt} prefix="" suffix="hrs" step="0.5"
            note="After this threshold → DT rate on Saturday"
            onChange={(v: number) => setStore(p => ({ ...p, satFirstHoursOt: v }))} />
          <Field label="Saturday OT multiplier" value={store.satMultiplier} prefix="" suffix="× ord. rate" step="0.25"
            onChange={(v: number) => setStore(p => ({ ...p, satMultiplier: v }))} />
          <Field label="Saturday DT multiplier" value={store.satDtMultiplier} prefix="" suffix="× ord. rate" step="0.25"
            onChange={(v: number) => setStore(p => ({ ...p, satDtMultiplier: v }))} />
          <Field label="Sunday — all hours" value={store.sunMultiplier} prefix="" suffix="× ord. rate" step="0.25"
            note="Applied to all Sunday hours regardless of total"
            onChange={(v: number) => setStore(p => ({ ...p, sunMultiplier: v }))} />
        </div>
      </div>

      {/* ── SECTION 5: Allowances ─────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 text-sm">Allowances</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="Tool allowance" value={store.allowances.toolAllowance} suffix="/week"
            note="Payable to trades employees who supply own tools"
            onChange={(v: number) => setStore(p => ({ ...p, allowances: { ...p.allowances, toolAllowance: v }}))} />
          <Field label="First aid officer" value={store.allowances.firstAidOfficer} suffix="/week"
            note="Where employee holds current first aid cert and is designated FAO"
            onChange={(v: number) => setStore(p => ({ ...p, allowances: { ...p.allowances, firstAidOfficer: v }}))} />
          <Field label="Leading hand" value={store.allowances.leadingHand} suffix="/week"
            note="Payable when employee is formally designated as leading hand"
            onChange={(v: number) => setStore(p => ({ ...p, allowances: { ...p.allowances, leadingHand: v }}))} />
          <Field label="Height work (above 6 m)" value={store.allowances.heightWork} suffix="/hr"
            note="Per hour worked at height above 6 metres"
            onChange={(v: number) => setStore(p => ({ ...p, allowances: { ...p.allowances, heightWork: v }}))} />
          <Field label="Confined space" value={store.allowances.confinedSpace} suffix="/hr"
            note="Per hour worked inside a confined space"
            onChange={(v: number) => setStore(p => ({ ...p, allowances: { ...p.allowances, confinedSpace: v }}))} />
        </div>
      </div>

      {/* ── SECTION 6: Industry levies ─────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 text-sm">Industry levies — employer obligations</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="CIPQ levy (portable LSL)" value={store.cipqLevyPct} prefix="" suffix="% of ord. wages" step="0.05"
            note="Construction Industry Portable Long Service Leave — paid to Q-COMP/CIPQ quarterly"
            onChange={(v: number) => setStore(p => ({ ...p, cipqLevyPct: v }))} />
          <Field label="BEWT levy" value={store.bewtLevyPct} prefix="" suffix="% of ord. wages" step="0.01"
            note="Building & Engineering Workers Training — paid quarterly"
            onChange={(v: number) => setStore(p => ({ ...p, bewtLevyPct: v }))} />
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800">
          These are employer-funded levies — they are not deducted from employee wages. They are calculated on gross ordinary wages and remitted to the relevant body each quarter. Include in payroll export for accounting reconciliation.
        </div>
      </div>

      {/* ── History ─────────────────────────────────────────────── */}
      {history.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button onClick={() => setHistory(!showHistory)}
            className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors">
            <span className="font-semibold text-sm text-gray-900">Rate change history ({history.length} previous versions)</span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
          </button>
          {showHistory && (
            <div className="divide-y border-t">
              {[...history].reverse().map((h, i) => (
                <div key={i} className="px-5 py-3 text-xs text-gray-600">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900">{h.effectiveDate}</span>
                    <span>C10: <strong>${h.c10BaseRate}/hr</strong></span>
                    <span>NMW: <strong>${h.nationalMinWage}/hr</strong></span>
                    <span>Night loading: <strong>{h.nightShiftLoadingPct}%</strong></span>
                    <span className="ml-auto text-gray-400">Updated by {h.updatedByName} · {new Date(h.updatedAt).toLocaleDateString('en-AU')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Save */}
      {isAdmin && (
        <div className="flex items-center gap-3 pt-2">
          <button onClick={handleSave}
            className="btn-primary flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Save pay rate configuration
          </button>
          <p className="text-xs text-gray-500">
            A rate history entry is created each time you save. Historical timesheets retain the rate that was active at approval.
          </p>

      </div>
      )}

      {/* Pay period P8 */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <h3 className="font-semibold text-gray-900 text-sm">Pay period (P8) — payroll frequency</h3>
        <div className="grid grid-cols-3 gap-3">
          {(['WEEKLY','FORTNIGHTLY','MONTHLY'] as const).map(p => (
            <button key={p} onClick={() => isAdmin && setPayPeriodSetting(p as any)} disabled={!isAdmin}
              className={`border-2 rounded-xl p-4 text-left transition-colors disabled:opacity-60 ${payPeriodSetting === p ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-200'}`}>
              <div className="font-semibold text-sm text-gray-900 mb-1">{p === 'WEEKLY' ? 'Weekly' : p === 'FORTNIGHTLY' ? 'Fortnightly' : 'Monthly'}</div>
              <div className="text-xs text-gray-500">{p === 'WEEKLY' ? 'Every 7 days (Mon to Sun)' : p === 'FORTNIGHTLY' ? 'Every 14 days. Standard for construction EBAs.' : 'Once per calendar month.'}</div>
              {payPeriodSetting === p && <div className="text-xs text-blue-600 mt-1">Active</div>}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400">Affects payroll export period, payslip label, and STP submission. Fortnightly is standard for most construction EBAs.</p>
      </div>

      {/* F3: Weekly hours cap */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <h3 className="font-semibold text-gray-900 text-sm">Weekly hours cap (F3) &amp; OT thresholds (P3)</h3>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Ordinary hours per week" value={store.weeklyOrdinaryHours} prefix="" suffix="h/wk"
            note="Full-time ordinary time (typically 38h)"
            onChange={(v: number) => setStore(p => ({ ...p, weeklyOrdinaryHours: v }))} />
          <Field label="Maximum weekly hours cap" value={store.weeklyHoursCap} prefix="" suffix="h/wk"
            note="Hard block at 100%, amber warning at 80%. Construction EBAs: 60-72h."
            onChange={(v: number) => setStore(p => ({ ...p, weeklyHoursCap: v }))} />
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800">
          Roster warns at {Math.round(store.weeklyHoursCap * 0.8)}h (80%) and blocks at {store.weeklyHoursCap}h. Update this cap if your EBA specifies a different maximum.
        </div>
        <div className="border-t pt-4">
          <p className="text-xs font-semibold text-gray-600 mb-3">Daily OT threshold by classification (P3)</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(store.classificationOTThresholds).map(([cls, thr]) => (
              <div key={cls}>
                <label className="block text-xs font-medium text-gray-700 mb-1">{cls.replace(/_/g,' ')}</label>
                <div className="flex items-center gap-1">
                  <input type="number" step="0.1" min="4" max="12" value={thr}
                    onChange={e => setStore(p => ({ ...p, classificationOTThresholds: { ...p.classificationOTThresholds, [cls]: parseFloat(e.target.value)||8 } }))}
                    disabled={!isAdmin} className="input text-sm w-full disabled:bg-gray-50" />
                  <span className="text-xs text-gray-400 flex-shrink-0">h/day</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  )
}


// ── SUPPORTED CURRENCIES ──────────────────────────────────────────
const CURRENCIES = [
  { code: 'AUD', label: 'Australian Dollar',  symbol: '$',  flag: 'AU' },
  { code: 'USD', label: 'US Dollar',           symbol: '$',  flag: 'US' },
  { code: 'EUR', label: 'Euro',                symbol: '€',  flag: 'EU' },
  { code: 'GBP', label: 'British Pound',       symbol: '£',  flag: 'GB' },
  { code: 'NZD', label: 'New Zealand Dollar',  symbol: '$',  flag: 'NZ' },
  { code: 'SGD', label: 'Singapore Dollar',    symbol: '$',  flag: 'SG' },
  { code: 'CAD', label: 'Canadian Dollar',     symbol: '$',  flag: 'CA' },
  { code: 'JPY', label: 'Japanese Yen',        symbol: '¥',  flag: 'JP' },
]

// ── CURRENCY TAB ──────────────────────────────────────────────────
function CurrencyTab({ isAdmin }: { isAdmin: boolean }) {
  const stored = typeof window !== 'undefined' ? localStorage.getItem('mcbce_currency') ?? 'AUD' : 'AUD'
  const [current, setCurrent] = useState(stored)
  const [pending, setPending] = useState<string | null>(null)

  const currentMeta  = CURRENCIES.find(c => c.code === current)  ?? CURRENCIES[0]
  const pendingMeta  = CURRENCIES.find(c => c.code === pending)  ?? null

  function confirmSwitch() {
    if (!pending) return
    setCurrent(pending)
    localStorage.setItem('mcbce_currency', pending)
    setPending(null)
  }

  if (!isAdmin) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-4">
        <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-amber-900">Tier A access required</p>
          <p className="text-sm text-amber-700 mt-1">
            Currency settings can only be changed by Tier A administrators.
            The platform base currency is <strong>AUD (Australian Dollar)</strong>.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Current currency */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-1">Base currency</h3>
        <p className="text-sm text-gray-500 mb-4">
          All platform financials, documents, and exports are denominated in this currency.
          Changing currency does not convert historical values.
        </p>
        <div className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
            {currentMeta.symbol}
          </div>
          <div>
            <div className="font-bold text-gray-900 text-lg">{currentMeta.code}</div>
            <div className="text-sm text-gray-600">{currentMeta.label}</div>
          </div>
          {current === 'AUD' && (
            <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
              Platform default
            </span>
          )}
        </div>
      </div>

      {/* Currency selector */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-1">Switch currency</h3>
        <p className="text-sm text-gray-500 mb-4">
          Select a new base currency. A confirmation window will appear before the change takes effect.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CURRENCIES.map(c => {
            const isActive = c.code === current
            return (
              <button key={c.code}
                onClick={() => !isActive && setPending(c.code)}
                disabled={isActive}
                className={`border rounded-xl p-4 text-left transition-colors ${
                  isActive
                    ? 'border-blue-400 bg-blue-50 cursor-default'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 cursor-pointer'
                }`}>
                <div className="text-xl font-bold text-gray-900">{c.symbol}</div>
                <div className="font-semibold text-sm text-gray-900 mt-1">{c.code}</div>
                <div className="text-xs text-gray-500 leading-tight">{c.label}</div>
                {isActive && <div className="text-xs text-blue-600 font-medium mt-1">Current</div>}
              </button>
            )
          })}
        </div>
      </div>

      {/* Warning box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          MCB Civil Electrical operates in Australia. The platform is designed and calibrated for
          AUD. Switching to a foreign currency affects all new documents, invoices, POs, and cost
          reports. Historical records are not converted.
        </p>
      </div>

      {/* Confirmation modal */}
      {pending && pendingMeta && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b bg-amber-50 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <h2 className="font-semibold text-gray-900">Confirm currency change</h2>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700">
                You are about to change the platform base currency from{' '}
                <strong>{currentMeta.code} — {currentMeta.label}</strong> to{' '}
                <strong>{pendingMeta.code} — {pendingMeta.label}</strong>.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                <strong>This action affects all new financial documents</strong> — invoices,
                purchase orders, estimates, progress claims, and cost reports. Historical
                records will not be updated. This change is reversible.
              </div>
              <p className="text-sm text-gray-500">
                Confirm you understand this change and wish to proceed.
              </p>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button onClick={() => setPending(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={confirmSwitch}
                className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-semibold">
                Confirm — switch to {pendingMeta.code}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


